// Configuração dos formulários dinâmicos de Briefing, por tipo de projeto.
// As respostas são armazenadas no campo `dados` (JSON) do Briefing.
//
// Tipos:
//   - SAAS_PRAGAS: sistema de gestão para controle de pragas (docs/breafing.md, focado no MVP)
//   - SITE: site institucional / landing page
//   - OUTRO: projeto genérico
//
// Sem identidade visual/logotipo — essas definições são feitas internamente.

export type TipoCampo = 'texto' | 'textarea' | 'checklist';

export interface CampoBriefing {
  id: string;
  label: string;
  tipo: TipoCampo;
  placeholder?: string;
  obrigatorio?: boolean;
  opcoes?: string[]; // apenas para checklist
}

export interface SecaoBriefing {
  titulo: string;
  descricao?: string;
  campos: CampoBriefing[];
}

export interface CampoAnexo {
  id: string;
  label: string;
}

export type TipoProjeto = 'SAAS_PRAGAS' | 'SITE' | 'OUTRO';

export interface ConfigBriefing {
  secoes: SecaoBriefing[];
  anexos: CampoAnexo[];
}

export const TIPOS_PROJETO: { id: TipoProjeto; label: string; descricao: string }[] = [
  { id: 'SAAS_PRAGAS', label: 'SaaS — Controle de Pragas', descricao: 'Sistema de gestão para empresas de controle de pragas' },
  { id: 'SITE', label: 'Site Institucional', descricao: 'Site ou landing page para o negócio' },
  { id: 'OUTRO', label: 'Outro Projeto', descricao: 'Outro tipo de projeto ou demanda' },
];

const SECOES_SAAS_PRAGAS: SecaoBriefing[] = [
  {
    titulo: '1. Dados da Empresa',
    campos: [
      { id: 'razaoSocial', label: 'Razão Social', tipo: 'texto', obrigatorio: true },
      { id: 'nomeFantasia', label: 'Nome Fantasia', tipo: 'texto' },
      { id: 'cnpj', label: 'CNPJ', tipo: 'texto' },
      { id: 'telefone', label: 'Telefone / WhatsApp', tipo: 'texto' },
      { id: 'email', label: 'E-mail Comercial', tipo: 'texto' },
    ],
  },
  {
    titulo: '2. Serviços e Pragas-alvo',
    campos: [
      {
        id: 'servicosPrestados',
        label: 'Serviços Prestados',
        tipo: 'checklist',
        opcoes: ['Desinsetização', 'Desratização', 'Descupinização', 'Controle de Pombos', 'Limpeza de Reservatórios', 'Sanitização de Ambientes'],
      },
      {
        id: 'pragasAlvo',
        label: 'Pragas-alvo',
        tipo: 'checklist',
        opcoes: ['Baratas', 'Ratos', 'Cupins', 'Formigas', 'Pulgas', 'Mosquitos', 'Pombos'],
      },
      { id: 'outrasInfo2', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '3. Precificação e Contratos',
    campos: [
      {
        id: 'tipoCobranca',
        label: 'Tipo de Cobrança',
        tipo: 'checklist',
        opcoes: ['Valor Fixo', 'Por m²', 'Por ambiente', 'Por isca', 'Por litro de calda'],
      },
      {
        id: 'criteriosPreco',
        label: 'Critérios que influenciam o preço',
        tipo: 'checklist',
        opcoes: ['Nível de infestação', 'Tipo de imóvel', 'Acessibilidade'],
      },
      { id: 'descontosContratos', label: 'Descontos progressivos para contratos', tipo: 'texto', placeholder: 'Mensal, bimestral, trimestral...' },
      { id: 'garantia', label: 'Prazo de garantia padrão', tipo: 'texto' },
      {
        id: 'frequencia',
        label: 'Frequência recomendada de aplicação',
        tipo: 'checklist',
        opcoes: ['Mensal', 'Bimestral', 'Trimestral', 'Semestral'],
      },
      { id: 'vistoriasRetorno', label: 'Vistorias de retorno incluídas', tipo: 'texto', placeholder: 'Quantas e com qual periodicidade?' },
      { id: 'outrasInfo3', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '4. Propostas e Orçamentos',
    campos: [
      {
        id: 'formasPagamento',
        label: 'Formas de Pagamento',
        tipo: 'checklist',
        opcoes: ['Dinheiro', 'PIX', 'Cartão', 'Boleto'],
      },
      { id: 'parcelamento', label: 'Condições de Parcelamento', tipo: 'texto', placeholder: 'À vista, 2x, 3x...' },
      { id: 'descontoAvista', label: 'Desconto para pagamento à vista (%)', tipo: 'texto' },
      { id: 'prazoValidade', label: 'Prazo de validade do orçamento', tipo: 'texto', placeholder: 'Ex: 15 dias' },
      { id: 'multaCancelamento', label: 'Multa / taxa de cancelamento', tipo: 'texto' },
      { id: 'outrasInfo4', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '5. Gestão de Clientes (CRM)',
    campos: [
      {
        id: 'camposObrigatorios',
        label: 'Campos obrigatórios no cadastro',
        tipo: 'checklist',
        opcoes: ['Nome / Razão Social', 'CPF / CNPJ', 'Telefone', 'WhatsApp', 'E-mail', 'Endereço do local de atendimento', 'Pessoa de contato'],
      },
      {
        id: 'tipoEstabelecimento',
        label: 'Tipos de estabelecimento atendidos',
        tipo: 'checklist',
        opcoes: ['Residencial', 'Comercial', 'Industrial'],
      },
      {
        id: 'camposComplementares',
        label: 'Campos complementares importantes',
        tipo: 'checklist',
        opcoes: ['Inscrição Estadual', 'Endereço de cobrança', 'Ponto de referência', 'Rotas de atendimento', 'Observações internas', 'Observações técnicas para O.S.', 'Locais tratados no estabelecimento'],
      },
      {
        id: 'origemContato',
        label: 'Origem do contato (Marketing)',
        tipo: 'checklist',
        opcoes: ['Indicação', 'Google / Busca Orgânica', 'Google Ads', 'Instagram', 'Facebook', 'WhatsApp Business', 'Passagem de frota', 'Panfleto / Folder', 'Site', 'Parcerias', 'Telefone'],
      },
      { id: 'outrasInfo5', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '6. Funil de Vendas',
    campos: [
      {
        id: 'etapasProcesso',
        label: 'Etapas do processo comercial',
        tipo: 'textarea',
        placeholder: 'Ex: Lead recebido → Qualificação → Visita técnica → Orçamento → Negociação → Ganho/Perdido',
      },
      {
        id: 'motivosPerda',
        label: 'Motivos de perda',
        tipo: 'checklist',
        opcoes: ['Preço alto', 'Escolheu concorrente', 'Decidiu não contratar', 'Sem retorno do cliente', 'Fora da área de atendimento'],
      },
      { id: 'visitaTecnica', label: 'Visita Técnica', tipo: 'texto', placeholder: 'É obrigatória? Há cobrança pela visita?' },
      { id: 'outrasInfo6', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '7. Ordens de Serviço (O.S.)',
    campos: [
      {
        id: 'informacoesOs',
        label: 'Informações que devem constar na O.S.',
        tipo: 'checklist',
        opcoes: ['Número da O.S.', 'Data e hora do agendamento', 'Cliente', 'Serviços executados', 'Técnico responsável', 'Produtos utilizados', 'Locais tratados', 'Observações técnicas', 'Assinatura do cliente', 'Fotos antes/depois', 'Próxima visita agendada'],
      },
      {
        id: 'fluxoOs',
        label: 'Fluxo da O.S.',
        tipo: 'textarea',
        placeholder: 'Ex: Agendada → Em execução → Concluída → Cancelada',
      },
      { id: 'outrasInfo7', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '8. Equipe e Permissões',
    campos: [
      {
        id: 'cargos',
        label: 'Cargos / Funções',
        tipo: 'checklist',
        opcoes: ['Administrador', 'Gestor Comercial', 'Vendedor / Comercial Externo', 'Atendente / Recepcionista', 'Técnico de Campo', 'Financeiro'],
      },
      { id: 'regrasNegocio', label: 'Regras de Negócio', tipo: 'textarea', placeholder: 'Quem aprova descontos? Quem exclui clientes? Quem edita valores?...' },
      { id: 'outrasInfo8', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '9. Agendamento e Logística',
    campos: [
      { id: 'areaAtendimento', label: 'Área de Atendimento', tipo: 'textarea', placeholder: 'Cidades/regiões atendidas, taxa de deslocamento, raio em km...' },
      { id: 'horarios', label: 'Horários de Atendimento', tipo: 'textarea', placeholder: 'Horário comercial, fins de semana, feriados...' },
      { id: 'equipe', label: 'Equipe e Veículos', tipo: 'textarea', placeholder: 'Quantidade de técnicos, veículos, necessidade de controle de rotas...' },
      { id: 'outrasInfo9', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '10. Ferramentas e Sistemas Atuais',
    campos: [
      { id: 'sistemasAtuais', label: 'Sistemas Atuais', tipo: 'textarea', placeholder: 'Usa algum sistema? Planilhas? WhatsApp Business?' },
      { id: 'outrasInfo10', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '11. Prioridades para o MVP',
    campos: [
      {
        id: 'prioridadeAlta',
        label: 'Prioridade ALTA (essencial para começar)',
        tipo: 'checklist',
        opcoes: ['Cadastro de Clientes', 'Cadastro de Serviços', 'Geração de Orçamentos/Propostas', 'Controle de Leads (funil de vendas)', 'Dashboard com indicadores'],
      },
      { id: 'outrasInfo11', label: 'Outras informações', tipo: 'textarea' },
    ],
  },
  {
    titulo: '12. Observações e Requisitos Específicos',
    campos: [
      { id: 'observacoes', label: 'Observações', tipo: 'textarea', placeholder: 'Descreva qualquer necessidade específica ou processo particular...' },
    ],
  },
];

const ANEXOS_SAAS_PRAGAS: CampoAnexo[] = [
  { id: 'proposta', label: 'Exemplo de proposta comercial' },
  { id: 'contrato', label: 'Exemplo de contrato de prestação de serviço' },
  { id: 'planilhaClientes', label: 'Planilha de clientes' },
  { id: 'tabelaPrecos', label: 'Tabela de preços / serviços' },
  { id: 'ordemServico', label: 'Exemplo de Ordem de Serviço' },
  { id: 'alvara', label: 'Alvará / Licenças' },
];

const SECOES_SITE: SecaoBriefing[] = [
  {
    titulo: '1. Dados da Empresa',
    campos: [
      { id: 'razaoSocial', label: 'Razão Social', tipo: 'texto', obrigatorio: true },
      { id: 'nomeFantasia', label: 'Nome Fantasia', tipo: 'texto' },
      { id: 'cnpj', label: 'CNPJ', tipo: 'texto' },
      { id: 'telefone', label: 'Telefone / WhatsApp', tipo: 'texto' },
      { id: 'email', label: 'E-mail Comercial', tipo: 'texto' },
    ],
  },
  {
    titulo: '2. Objetivo do Site',
    campos: [
      { id: 'objetivoSite', label: 'Objetivo principal do site', tipo: 'textarea', obrigatorio: true, placeholder: 'Apresentar a empresa, captar leads, vender, agendar...' },
    ],
  },
  {
    titulo: '3. Páginas e Seções',
    campos: [
      {
        id: 'paginas',
        label: 'Páginas / Seções desejadas',
        tipo: 'checklist',
        opcoes: ['Home', 'Sobre', 'Serviços', 'Contato', 'Blog / Notícias', 'Portfólio / Galeria', 'Depoimentos', 'FAQ', 'Orçamento online'],
      },
    ],
  },
  {
    titulo: '4. Funcionalidades',
    campos: [
      {
        id: 'funcionalidades',
        label: 'Funcionalidades desejadas',
        tipo: 'checklist',
        opcoes: ['Formulário de contato', 'Botão de WhatsApp', 'Blog / Notícias', 'Área do cliente', 'Multi-idioma', 'Integração com redes sociais', 'Mapa / Google Maps'],
      },
    ],
  },
  {
    titulo: '5. Conteúdo',
    campos: [
      { id: 'conteudo', label: 'Conteúdo existente', tipo: 'textarea', placeholder: 'Já possui textos, fotos ou descrições de serviços?' },
      { id: 'referencias', label: 'Sites de referência', tipo: 'textarea', placeholder: 'Sites que você gosta ou que servem de referência...' },
    ],
  },
  {
    titulo: '6. Integrações e Métricas',
    campos: [
      {
        id: 'integracoesSite',
        label: 'Integrações desejadas',
        tipo: 'checklist',
        opcoes: ['Google Analytics', 'Facebook Pixel', 'Instagram', 'E-mail marketing', 'Google Ads'],
      },
    ],
  },
  {
    titulo: '7. Observações',
    campos: [
      { id: 'observacoes', label: 'Observações', tipo: 'textarea', placeholder: 'Qualquer requisito específico...' },
    ],
  },
];

const ANEXOS_SITE: CampoAnexo[] = [
  { id: 'conteudoSite', label: 'Conteúdo / textos existentes' },
  { id: 'referenciasSite', label: 'Referências / exemplos de sites' },
];

const SECOES_OUTRO: SecaoBriefing[] = [
  {
    titulo: '1. Dados da Empresa',
    campos: [
      { id: 'razaoSocial', label: 'Razão Social', tipo: 'texto', obrigatorio: true },
      { id: 'telefone', label: 'Telefone / WhatsApp', tipo: 'texto' },
      { id: 'email', label: 'E-mail Comercial', tipo: 'texto' },
    ],
  },
  {
    titulo: '2. Descrição do Projeto',
    campos: [
      { id: 'descricaoProjeto', label: 'Descreva o projeto', tipo: 'textarea', obrigatorio: true, placeholder: 'O que você precisa?' },
    ],
  },
  {
    titulo: '3. Objetivos e Requisitos',
    campos: [
      { id: 'objetivos', label: 'Objetivos esperados', tipo: 'textarea', placeholder: 'Resultados que espera alcançar...' },
      { id: 'requisitos', label: 'Requisitos / funcionalidades', tipo: 'textarea', placeholder: 'Liste o que é essencial...' },
    ],
  },
  {
    titulo: '4. Observações',
    campos: [
      { id: 'observacoes', label: 'Observações', tipo: 'textarea', placeholder: 'Qualquer informação relevante...' },
    ],
  },
];

const ANEXOS_OUTRO: CampoAnexo[] = [
  { id: 'documentos', label: 'Documentos de referência' },
];

export const CONFIGS_BRIEFING: Record<TipoProjeto, ConfigBriefing> = {
  SAAS_PRAGAS: { secoes: SECOES_SAAS_PRAGAS, anexos: ANEXOS_SAAS_PRAGAS },
  SITE: { secoes: SECOES_SITE, anexos: ANEXOS_SITE },
  OUTRO: { secoes: SECOES_OUTRO, anexos: ANEXOS_OUTRO },
};

// Alias para compatibilidade (tipo padrão)
export const SECOES_BRIEFING = SECOES_SAAS_PRAGAS;
export const CAMPOS_ANEXOS = ANEXOS_SAAS_PRAGAS;
